import { Alert, Button, Group, NumberInput, Stack, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconFileTypeXls, IconInfoCircle } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WosIsiReportOrgFilter, useWosIsiOrgSelection } from '@/module/WosIsiReport/components/WosIsiReportOrgFilter';
import { useGenerateWosIsiReport } from '@/module/WosIsiReport/model/WosIsiReportQuery';
import {
  WosIndexingType,
  WosIsiReportParams,
  WosIsiReportType,
  WosType,
} from '@/module/WosIsiReport/model/WosIsiReportTypes';

import { CustomSelect } from '@/core/components/Select';

import { RoleType, useCurrentUser } from '@feature/security';
import { range } from '@feature/utils';
import { ValidationStatus } from '@feature/validation';

export interface WosIsiReportFormProps {
  /** which of the two reports to offer; defaults to both */
  reports?: WosIsiReportType[];
}

const currentYear = new Date().getFullYear();
const MIN_YEAR = 1997;

const monthValues = range(12, 1);

export function WosIsiReportForm({ reports = ['isi-articles', 'isi-abstracts'] }: WosIsiReportFormProps) {
  const { t } = useTranslation();
  const { currentRole } = useCurrentUser();

  const [startYearStr, setStartYearStr] = useState<number | string>(String(currentYear - 1));
  const [endYearStr, setEndYearStr] = useState<number | string>(String(currentYear - 1));
  const [startMonth, setStartMonth] = useState<string>('1');
  const [endMonth, setEndMonth] = useState<string>('12');
  const [wosType, setWosType] = useState<WosType>('CORE_COLLECTION');
  const [indexingType, setIndexingType] = useState<WosIndexingType>('PUBLISHING_YEAR');
  const [validationStatus, setValidationStatus] = useState<ValidationStatus | null>(
    currentRole === RoleType.AUTHOR ? null : 'VALIDATED',
  );
  const [filterAfterAffiliation, setFilterAfterAffiliation] = useState<boolean>(true);

  const organisation = useWosIsiOrgSelection();

  const articlesReport = useGenerateWosIsiReport('isi-articles');
  const abstractsReport = useGenerateWosIsiReport('isi-abstracts');

  const isMonthApplicable = indexingType === 'PUBLISHING_YEAR';

  // NumberInput lets partial input ('-', '') through, so anything not finite counts as missing
  const toYear = (value: number | string) => {
    const year = Number(value);
    return value !== '' && Number.isFinite(year) ? year : null;
  };

  const startYear = toYear(startYearStr);
  const endYear = toYear(endYearStr);

  const monthOptions = useMemo(
    () => monthValues.map(month => ({ value: String(month), label: t(`wosIsiReport.month.${month}`) })),
    [t],
  );

  const wosTypeOptions = useMemo(
    () => [
      { value: 'CORE_COLLECTION', label: t('wosIsiReport.wosTypeOptions.CORE_COLLECTION') },
      { value: 'EXPANDED', label: t('wosIsiReport.wosTypeOptions.EXPANDED') },
    ],
    [t],
  );

  const indexingTypeOptions = useMemo(
    () => [
      { value: 'PUBLISHING_YEAR', label: t('wosIsiReport.indexingTypeOptions.PUBLISHING_YEAR') },
      { value: 'WOS_INDEXING_YEAR', label: t('wosIsiReport.indexingTypeOptions.WOS_INDEXING_YEAR') },
    ],
    [t],
  );

  const validationOptions = useMemo(
    () => [
      { value: 'VALIDATED', label: t('wosIsiReport.validationOptions.VALIDATED') },
      { value: 'REJECTED', label: t('wosIsiReport.validationOptions.REJECTED') },
    ],
    [t],
  );

  const yearError = (() => {
    if (startYear == null || endYear == null) return t('wosIsiReport.errors.yearRequired');
    if (endYear < startYear) return t('wosIsiReport.errors.yearRange');
    return null;
  })();

  const monthError =
    isMonthApplicable && yearError == null && startYear === endYear && Number(endMonth) < Number(startMonth)
      ? t('wosIsiReport.errors.monthRange')
      : null;

  const isInvalid = yearError != null || monthError != null;
  const isPending = articlesReport.isPending || abstractsReport.isPending;

  const showError = (error: unknown) => {
    const status = isAxiosError(error) ? error.response?.status : undefined;

    let message = t('wosIsiReport.errors.generic');
    if (status === 403) {
      message = t('wosIsiReport.errors.forbidden');
    } else if (status === 400) {
      message = t('wosIsiReport.errors.badRequest');
    }

    notifications.show({
      color: 'red',
      title: t('notification.error.title'),
      message,
    });
  };

  const handleDownload = (type: WosIsiReportType) => {
    if (isInvalid || startYear == null || endYear == null) return;

    const params: WosIsiReportParams = {
      startYear,
      endYear,
      startMonth: isMonthApplicable ? Number(startMonth) : null,
      endMonth: isMonthApplicable ? Number(endMonth) : null,
      wosType,
      indexingType,
      facultyId: organisation.facultyId,
      departmentId: organisation.departmentId,
      disciplineId: organisation.disciplineId,
      userId: organisation.userId,
      validationStatus,
      filterAfterAffiliation,
    };

    const mutation = type === 'isi-articles' ? articlesReport : abstractsReport;
    mutation.mutate(params, { onError: showError });
  };

  return (
    <Stack>
      <Group align="flex-start">
        <NumberInput
          w={120}
          size="xs"
          allowDecimal={false}
          allowNegative={false}
          min={MIN_YEAR}
          max={currentYear}
          label={t('wosIsiReport.startYear')}
          value={startYearStr}
          onChange={setStartYearStr}
          error={yearError != null}
        />
        <NumberInput
          w={120}
          size="xs"
          allowDecimal={false}
          allowNegative={false}
          min={MIN_YEAR}
          max={currentYear}
          label={t('wosIsiReport.endYear')}
          value={endYearStr}
          onChange={setEndYearStr}
          error={yearError}
        />
        <CustomSelect
          w={150}
          size="xs"
          label={t('wosIsiReport.startMonth')}
          data={monthOptions}
          value={startMonth}
          onChange={value => setStartMonth(value ?? '1')}
          disabled={!isMonthApplicable}
          allowDeselect={false}
        />
        <CustomSelect
          w={150}
          size="xs"
          label={t('wosIsiReport.endMonth')}
          data={monthOptions}
          value={endMonth}
          onChange={value => setEndMonth(value ?? '12')}
          disabled={!isMonthApplicable}
          allowDeselect={false}
          error={monthError}
        />
      </Group>

      <Group align="flex-end">
        <CustomSelect
          w={280}
          size="xs"
          label={t('wosIsiReport.wosType')}
          data={wosTypeOptions}
          value={wosType}
          onChange={value => setWosType((value as WosType) ?? 'CORE_COLLECTION')}
          allowDeselect={false}
        />
        <CustomSelect
          w={220}
          size="xs"
          label={t('wosIsiReport.indexingType')}
          description={t('wosIsiReport.indexingTypeDescription')}
          data={indexingTypeOptions}
          value={indexingType}
          onChange={value => setIndexingType((value as WosIndexingType) ?? 'PUBLISHING_YEAR')}
          allowDeselect={false}
        />
      </Group>

      <WosIsiReportOrgFilter />

      <Group align="flex-end">
        <CustomSelect
          w={220}
          size="xs"
          label={t('wosIsiReport.validationStatus')}
          description={t('wosIsiReport.validationStatusDescription')}
          placeholder={t('wosIsiReport.validationStatusPlaceholder')}
          data={validationOptions}
          value={validationStatus}
          onChange={status => setValidationStatus(status as ValidationStatus | null)}
          clearable
          clearButtonProps={{ 'aria-label': t('wosIsiReport.validationStatusClear'), 'aria-hidden': false }}
        />
        <Switch
          label={t('wosIsiReport.filterAfterAffiliation')}
          checked={filterAfterAffiliation}
          onChange={e => setFilterAfterAffiliation(e.currentTarget.checked)}
        />
      </Group>

      <Alert
        variant="light"
        color="blue"
        icon={<IconInfoCircle />}
      >
        <Stack gap="2px">
          <Text size="xs">{t('wosIsiReport.hints.inclusiveRange')}</Text>
          <Text size="xs">{t('wosIsiReport.hints.monthOnlyForPublishingYear')}</Text>
          <Text size="xs">{t('wosIsiReport.hints.noDayGranularity')}</Text>
        </Stack>
      </Alert>

      <Group>
        {reports.includes('isi-articles') && (
          <Button
            size="xs"
            leftSection={<IconFileTypeXls size="24px" />}
            disabled={isInvalid}
            loading={articlesReport.isPending}
            onClick={() => handleDownload('isi-articles')}
          >
            {t('wosIsiReport.articlesButton')}
          </Button>
        )}
        {reports.includes('isi-abstracts') && (
          <Button
            size="xs"
            leftSection={<IconFileTypeXls size="24px" />}
            disabled={isInvalid}
            loading={abstractsReport.isPending}
            onClick={() => handleDownload('isi-abstracts')}
          >
            {t('wosIsiReport.abstractsButton')}
          </Button>
        )}
      </Group>

      {isPending && (
        <Text
          size="xs"
          c="dimmed"
          fs="italic"
        >
          {t('wosIsiReport.generating')}
        </Text>
      )}
    </Stack>
  );
}
